import {useEffect, useMemo, useState} from 'react';
import {format} from 'date-fns';
import {
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  Maximize2,
  PanelRightClose,
  PanelRightOpen,
  Search,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {toast} from '@common-web/hooks/use-toast';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {canViewFile} from '@common/groupFtp/files';
import {FTP_SUBFOLDER_ROLE_LABELS, type FtpSubfolderRole} from '@common/groupFtp/types';
import {FileView, FileViewerDialog} from '@internal/pages/group-pipeline/detail/ftp/FileViewer';
import {fileUrl} from '@internal/pages/group-pipeline/detail/ftp/useDocumentFile';

const fileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// Pick a file already in the document's destination folder as its file.
export function LinkDocumentFileDialog({
  vbaGroupId,
  doc,
  onOpenChange,
}: {
  vbaGroupId: string;
  doc: {id: string; label: string; ftpPath: string | null; ftpDestination: FtpSubfolderRole};
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const files = trpc.internal.groupFtp.destinationFiles.useQuery({
    vbaGroupId,
    role: doc.ftpDestination,
  });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(doc.ftpPath);
  // While open, the preview shows whichever file is selected.
  const [previewOpen, setPreviewOpen] = useState(false);

  const link = trpc.internal.groupFtp.linkDocumentFile.useMutation({
    onSuccess: async ({fileName}) => {
      await Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupFtp.destinationFiles.invalidate({vbaGroupId}),
        utils.internal.groupFtp.missingFiles.invalidate({vbaGroupId}),
      ]);
      toast({title: `${fileName} linked as the ${doc.label}`});
      onOpenChange(false);
    },
  });

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (files.data?.files ?? []).filter((f) => !term || f.name.toLowerCase().includes(term));
  }, [files.data, search]);
  const folder = files.data?.folders[0];

  const preview = (path: string) => {
    setSelected(path);
    setPreviewOpen(true);
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'grid-cols-[minmax(0,1fr)] transition-[max-width] duration-300 ease-out',
          previewOpen ? 'sm:max-w-5xl' : 'sm:max-w-xl',
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {doc.ftpPath ? `Change ${doc.label} file` : `Link ${doc.label}`}
          </DialogTitle>
          <DialogDescription>
            Pick a file from the group's{' '}
            {folder ? (
              <span title={folder} className='font-medium text-gray-700'>
                {folder.split('/').pop()}
              </span>
            ) : (
              FTP_SUBFOLDER_ROLE_LABELS[doc.ftpDestination].toLowerCase()
            )}{' '}
            folder.
          </DialogDescription>
        </DialogHeader>

        {/* Closed, the preview is just its toggle beside the filter and the list runs full width;
            opening slides it out over the list's right side. */}
        <div
          className='grid h-[26rem] grid-rows-[auto_minmax(0,1fr)] gap-3 transition-[grid-template-columns] duration-300 ease-out'
          style={{gridTemplateColumns: `minmax(0,1fr) ${previewOpen ? '420px' : '2.5rem'}`}}
        >
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Filter files'
              aria-label='Filter files'
              className='pl-9'
            />
          </div>

          <FilePreview
            vbaGroupId={vbaGroupId}
            role={doc.ftpDestination}
            path={selected}
            open={previewOpen}
            onToggle={() => setPreviewOpen((open) => !open)}
          />

          <div className={cn('row-start-2 flex min-h-0 flex-col', !previewOpen && 'col-span-2')}>
            {files.error ? (
              <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
                {files.error.message}
              </p>
            ) : (
              <ul className='flex flex-1 flex-col divide-y overflow-y-auto rounded-md border'>
                {files.isLoading && (
                  <li className='flex flex-1 items-center justify-center gap-2 text-sm text-gray-500'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Opening the folder
                  </li>
                )}
                {files.data && files.data.folders.length === 0 && (
                  <li className='flex flex-1 items-center justify-center px-6 text-center text-sm text-gray-500'>
                    No subfolder is set for{' '}
                    {FTP_SUBFOLDER_ROLE_LABELS[doc.ftpDestination].toLowerCase()}. Set one in Manage
                    folder mapping.
                  </li>
                )}
                {files.data && files.data.folders.length > 0 && shown.length === 0 && (
                  <li className='flex flex-1 items-center justify-center text-sm text-gray-500'>
                    {search ? 'No files match' : 'No files in this folder'}
                  </li>
                )}
                {shown.map((f) => {
                  const takenBy = f.linkedTo && f.linkedTo.id !== doc.id ? f.linkedTo.label : null;
                  const previewing = previewOpen && selected === f.path;
                  return (
                    <li
                      key={f.path}
                      className={cn('flex items-center', selected === f.path && 'bg-blue-50')}
                    >
                      <button
                        type='button'
                        disabled={Boolean(takenBy)}
                        aria-pressed={selected === f.path}
                        onClick={() => setSelected(f.path)}
                        className='flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:text-gray-400'
                      >
                        <FileText className='h-4 w-4 shrink-0 text-gray-400' />
                        <span className='min-w-0 flex-1 truncate'>{f.name}</span>
                        <span className='shrink-0 text-xs text-gray-500'>
                          {takenBy
                            ? `Linked to ${takenBy}`
                            : `${format(new Date(f.modifiedAt), 'MMM d, yyyy')} · ${fileSize(f.size)}`}
                        </span>
                      </button>
                      {!takenBy && canViewFile(f.name) ? (
                        <Button
                          variant='ghost'
                          size='icon'
                          aria-label={`Preview ${f.name}`}
                          title='Preview'
                          onClick={() => preview(f.path)}
                          className={cn(
                            'mr-1 h-7 w-7 shrink-0',
                            previewing && 'bg-blue-100 text-blue-700 hover:bg-blue-100',
                          )}
                        >
                          <Eye className='h-4 w-4' />
                        </Button>
                      ) : (
                        <span className='mr-1 w-7 shrink-0' />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {link.error && (
          <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
            {link.error.message}
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!selected || selected === doc.ftpPath || link.isPending}
            onClick={() =>
              selected && link.mutate({vbaGroupId, documentId: doc.id, path: selected})
            }
          >
            {link.isPending && <Loader2 className='h-4 w-4 animate-spin' />}
            Link file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilePreview({
  vbaGroupId,
  role,
  path,
  open,
  onToggle,
}: {
  vbaGroupId: string;
  role: FtpSubfolderRole;
  path: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  const name = path?.split('/').pop() ?? null;
  const viewable = Boolean(name && canViewFile(name));
  const file = trpc.internal.groupFtp.previewFile.useQuery(
    {vbaGroupId, role, path: path ?? ''},
    {enabled: open && viewable, staleTime: Infinity, gcTime: 60_000},
  );
  const utils = trpc.useUtils();
  useEffect(() => {
    if (file.dataUpdatedAt) void utils.internal.groupPipeline.activity.invalidate({vbaGroupId});
  }, [file.dataUpdatedAt, utils, vbaGroupId]);
  const url = useMemo(() => (file.data ? fileUrl(file.data) : null), [file.data]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  const [fullScreen, setFullScreen] = useState(false);
  const shown =
    viewable && url && file.data ? {url, name, contentType: file.data.contentType} : null;

  return (
    <>
      <div className='col-start-2 row-start-1 flex min-w-0 items-center gap-2 overflow-hidden'>
        <Button
          variant='outline'
          size='icon'
          aria-pressed={open}
          aria-label={open ? 'Hide preview' : 'Show preview'}
          title={open ? 'Hide preview' : 'Show preview'}
          onClick={onToggle}
          className={cn('shrink-0', open && 'bg-blue-50 text-blue-700 hover:bg-blue-50')}
        >
          {open ? <PanelRightClose className='h-4 w-4' /> : <PanelRightOpen className='h-4 w-4' />}
        </Button>
        {open && (
          <>
            <p className='min-w-0 flex-1 truncate text-sm font-medium'>{name ?? 'Preview'}</p>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 shrink-0'
              aria-label='Open in new tab'
              title='Open in new tab'
              disabled={!shown}
              onClick={() => shown && window.open(shown.url, '_blank')}
            >
              <ExternalLink className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 shrink-0'
              aria-label='Full screen'
              title='Full screen'
              disabled={!shown}
              onClick={() => setFullScreen(true)}
            >
              <Maximize2 className='h-4 w-4' />
            </Button>
          </>
        )}
      </div>
      {open && (
        <div className='col-start-2 row-start-2 flex min-w-0 items-center justify-center overflow-hidden rounded-md bg-gray-100'>
          {!name ? (
            <p className='px-6 text-center text-sm text-gray-500'>
              Select a PDF or image to preview it.
            </p>
          ) : !viewable ? (
            <p className='px-6 text-center text-sm text-gray-500'>No preview for this file type.</p>
          ) : file.error ? (
            <p className='px-6 text-center text-sm text-red-700'>{file.error.message}</p>
          ) : !shown ? (
            <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
          ) : (
            <FileView {...shown} />
          )}
        </div>
      )}

      {shown && fullScreen && <FileViewerDialog file={shown} onOpenChange={setFullScreen} />}
    </>
  );
}
